import { Location } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import * as fromReduce from '@employer/app/store/reducers'
import { AccountSessionService } from '@employer/app/services/account-session.service'
import { ImpersonationExchangeService } from '@employer/app/services/impersonation-exchange.service'
import { AppConfigService } from '@engineering11/tenant-bootstrap-web'
import { IAuthService } from '@engineering11/auth-web'
import { NotificationHelper } from '@engineering11/web-ui-helpers'
import { Store } from '@ngrx/store'
import jwt_decode from 'jwt-decode'
import { userActions } from 'shared-lib'

// True while an exchange is in flight in this page instance, so a router re-run of this component (the
// code is already stripped from the URL by then) waits quietly instead of reporting a spurious failure.
// Deliberately in memory, not storage: a reload aborts the request, and a persisted marker would leave
// the reloaded page stuck on the loader with nothing left to clear it.
let exchangeInFlight = false
export const isActAsExchangeInFlight = () => exchangeInFlight
export const resetActAsExchangeState = () => void (exchangeInFlight = false)

// Short non-cryptographic hash so the one-time code itself never lands in sessionStorage.
export function hashCode(value: string): string {
  let hash = 5381
  for (let i = 0; i < value.length; i++) hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0
  return (hash >>> 0).toString(36)
}

/**
 * Unguarded handoff route consumed by the admin app's "Act as" flow: admin opens
 * `<employerUrl>#/act-as?code=…` in a new tab. This component exchanges the one-time
 * `code` for a Firebase custom token (public call, no signed-in user yet), signs in, then
 * reuses the existing `userActions.OnLogIn` login flow to store the token, load the user
 * doc, and navigate to `home` — exactly as `login.component.ts` does after an
 * email/password sign-in. Do not hand-roll any of that here.
 */
@Component({
  templateUrl: './act-as.component.html',
  styleUrls: ['./act-as.component.scss'],
})
export class ActAsComponent implements OnInit {
  logoUrl = this.appConfig.config.brand.logoImageDarkUrl

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private store: Store<fromReduce.user.State>,
    private authService: IAuthService,
    private exchangeService: ImpersonationExchangeService,
    private notifications: NotificationHelper,
    private appConfig: AppConfigService,
    private accountSession: AccountSessionService
  ) {}

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code')

    if (!code) {
      // No code but an exchange is already running in this tab: a re-run after the URL was rewritten.
      if (exchangeInFlight) return
      this.fail()
      return
    }

    // Drop the code from the URL (and this tab's history entry) before any async work so it cannot be
    // re-read from history, analytics page_location, or error-reporter URL capture.
    this.location.replaceState('/act-as')

    // The impersonation code is single-use. Guard by a hash of the code so it is exchanged at most
    // once even if this component is instantiated again with the same URL.
    const guardKey = `actAsExchanged:${hashCode(code)}`
    if (this.read(guardKey)) return

    exchangeInFlight = true
    this.exchangeService.exchange(code).subscribe({
      next: ({ token }) => {
        this.write(guardKey)
        this.signIn(token)
      },
      error: () => this.fail(),
    })
  }

  private async signIn(customToken: string): Promise<void> {
    let signedIn = false
    try {
      this.accountSession.expectAccountChange()
      const { res, err } = await this.authService.customTokenSignIn(customToken)
      if (err || !res) {
        this.accountSession.cancelExpectedAccountChange()
        this.fail()
        return
      }
      signedIn = true

      const idToken = await res.user.getIdToken(true)
      const decoded: any = jwt_decode(idToken)
      this.store.dispatch(userActions.OnLogIn({ token: idToken, appUserId: decoded.appUserId, redirectPath: 'home' }))
    } catch {
      // Firebase may already be persisted as the shadow identity; never leave that behind a "failed" toast.
      if (signedIn) this.store.dispatch(userActions.OnLogOut())
      this.fail()
    }
  }

  private fail(): void {
    exchangeInFlight = false
    this.notifications.popNotification({
      type: 'error',
      title: 'Sign-in failed',
      message: 'This access link is invalid or has expired.',
    })
    this.router.navigate(['/auth/login'])
  }

  private read(key: string): boolean {
    try {
      return !!sessionStorage.getItem(key)
    } catch {
      return false
    }
  }

  private write(key: string): void {
    try {
      sessionStorage.setItem(key, '1')
    } catch {}
  }
}
